import React from 'react';
import Moment from 'moment';
import PropTypes from 'prop-types';
import { Grid } from 'semantic-ui-react';
import Datetime from 'react-datetime';
import { DATETIME_FORMAT } from '../../../constants';
import polyglotUIComponents from '../../../i18n';
import UiButton from '../../../uiButton';
import UiInputSemantic from '../../../uiInputSemantic';
import UiDatetimePicker from '../../../uiDatetimePicker';
import 'react-datetime/css/react-datetime.css';
import './index.css';

/**
 * Time range absolute
 * Parameters (Props):
 * @param nameButtonSend: PropTypes.string,
 * @param dateTimeIniAbsolute: PropTypes.objectOf(Object).isRequired,
 * @param dateTimeEndAbsolute: PropTypes.objectOf(Object).isRequired,
 * @param selectTimeFrame: PropTypes.func.isRequired,
 * behavior:
 * the variables dateTimeIniAbsolute and dateTimeEndAbsolute are associated with
 * the component DateTimeInput and the type is Moment
 * the variables dateTimeIniAbsoluteInput and dateTimeEndAbsoluteInput are associated
 * with Input components and the type is string.
 * Cange in the component DateTimeInput should change the value of the component
 * input associated
 */
export default class TimeRangeAbsolute extends React.PureComponent {
  /**
   * Is valid date
   * valid that the select date is not in the future
   * @param moment current
   */
  static isValidDate(current) {
    const today = Datetime.moment();
    return current.isBefore(today);
  }

  constructor(props) {
    super(props);
    this.viewTimeRangeAbsolute = this.viewTimeRangeAbsolute.bind(this);
    this.handleOnChangeDateTimeIni = this.handleOnChangeDateTimeIni.bind(this);
    this.handleOnChangeDateTimeEnd = this.handleOnChangeDateTimeEnd.bind(this);
    this.handleOnBlurDateTimeIni = this.handleOnBlurDateTimeIni.bind(this);
    this.handleOnBlurDateTimeEnd = this.handleOnBlurDateTimeEnd.bind(this);
    this.changeDateTimeIni = this.changeDateTimeIni.bind(this);
    this.changeDateTimeEnd = this.changeDateTimeEnd.bind(this);
    this.polyglot = polyglotUIComponents(props.language);
    this.state = {
      dateTimeIniAbsolute: props.dateTimeIniAbsolute,
      dateTimeEndAbsolute: props.dateTimeEndAbsolute,
      dateTimeIniAbsoluteInput:
        props.dateTimeIniAbsolute.format(DATETIME_FORMAT),
      dateTimeEndAbsoluteInput:
        props.dateTimeEndAbsolute.format(DATETIME_FORMAT),
    };
  }

  /**
   * Handle onchange date time ini
   * @param object event
   */
  handleOnChangeDateTimeIni(event) {
    this.setState({ dateTimeIniAbsoluteInput: event.target.value });
  }

  /**
   * Handle change date time emd
   * @param object event
   */
  handleOnChangeDateTimeEnd(event) {
    this.setState({ dateTimeEndAbsoluteInput: event.target.value });
  }

  /**
   * Handle on blur date time ini
   * @param string value
   */
  handleOnBlurDateTimeIni(value) {
    const dateTimeIniAbsoluteInput = Moment(value, DATETIME_FORMAT);
    if (dateTimeIniAbsoluteInput.isValid()) {
      this.setState({
        dateTimeIniAbsoluteInput:
          dateTimeIniAbsoluteInput.format(DATETIME_FORMAT),
        dateTimeIniAbsolute: dateTimeIniAbsoluteInput,
      });
    } else {
      const { dateTimeIniAbsolute } = this.state;
      this.setState({
        dateTimeIniAbsoluteInput: dateTimeIniAbsolute.format(DATETIME_FORMAT),
      });
    }
  }

  /**
   * Handle on blur date time end
   * @param string value
   */
  handleOnBlurDateTimeEnd(value) {
    const dateTimeEndAbsoluteInput = Moment(value, DATETIME_FORMAT);
    if (dateTimeEndAbsoluteInput.isValid()) {
      this.setState({
        dateTimeEndAbsoluteInput:
          dateTimeEndAbsoluteInput.format(DATETIME_FORMAT),
        dateTimeEndAbsolute: dateTimeEndAbsoluteInput,
      });
    } else {
      const { dateTimeEndAbsolute } = this.state;
      this.setState({
        dateTimeEndAbsoluteInput: dateTimeEndAbsolute.format(DATETIME_FORMAT),
      });
    }
  }

  changeDateTimeIni(dateTimeIniAbsolute) {
    this.setState({
      dateTimeIniAbsolute,
      dateTimeIniAbsoluteInput: dateTimeIniAbsolute.format(DATETIME_FORMAT),
    });
  }

  changeDateTimeEnd(dateTimeEndAbsolute) {
    this.setState({
      dateTimeEndAbsolute,
      dateTimeEndAbsoluteInput: dateTimeEndAbsolute.format(DATETIME_FORMAT),
    });
  }

  /**
   * View time range absolute
   */
  viewTimeRangeAbsolute() {
    const {
      dateTimeIniAbsolute,
      dateTimeEndAbsolute,
      dateTimeIniAbsoluteInput,
      dateTimeEndAbsoluteInput,
    } = this.state;
    const { nameButtonSend, selectTimeFrame, mode } = this.props;
    return (
      <Grid stackable>
        <Grid.Row className="time-range-absolute">
          <Grid.Column widescreen={7} computer={7} tablet={16} mobile={16}>
            <UiInputSemantic
              icon="calendar alternate outline"
              iconPosition="right"
              value={dateTimeIniAbsoluteInput}
              label={this.polyglot.t('general.from')}
              labelPosition="left"
              onChange={this.handleOnChangeDateTimeIni}
              onBlur={() =>
                this.handleOnBlurDateTimeIni(dateTimeIniAbsoluteInput)
              }
              mode={mode}
            />
            <UiDatetimePicker
              value={dateTimeIniAbsolute}
              onChange={this.changeDateTimeIni}
              input={false}
              open
              isValidDate={TimeRangeAbsolute.isValidDate}
              mode={mode}
            />
          </Grid.Column>
          <Grid.Column width={1} />
          <Grid.Column widescreen={7} computer={7} tablet={16} mobile={16}>
            <UiInputSemantic
              icon="calendar alternate outline"
              iconPosition="right"
              value={dateTimeEndAbsoluteInput}
              label={this.polyglot.t('general.to')}
              labelPosition="left"
              onChange={this.handleOnChangeDateTimeEnd}
              onBlur={() =>
                this.handleOnBlurDateTimeEnd(dateTimeEndAbsoluteInput)
              }
              mode={mode}
            />
            <UiDatetimePicker
              value={dateTimeEndAbsolute}
              onChange={this.changeDateTimeEnd}
              input={false}
              open
              isValidDate={TimeRangeAbsolute.isValidDate}
              mode={mode}
            />
          </Grid.Column>
          <Grid.Column width={16} textAlign="right">
            <UiButton
              primary
              label={
                nameButtonSend !== null
                  ? nameButtonSend
                  : this.polyglot.t('button.send')
              }
              onClick={() =>
                selectTimeFrame(dateTimeIniAbsolute, dateTimeEndAbsolute)
              }
              showLabels
              iconName="search"
              mode={mode}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }

  render() {
    const viewTimeRangeAbsolute = this.viewTimeRangeAbsolute();
    return viewTimeRangeAbsolute;
  }
}

TimeRangeAbsolute.propTypes = {
  nameButtonSend: PropTypes.string,
  dateTimeIniAbsolute: PropTypes.objectOf(Object).isRequired,
  dateTimeEndAbsolute: PropTypes.objectOf(Object).isRequired,
  selectTimeFrame: PropTypes.func.isRequired,
  mode: PropTypes.string,
  language: PropTypes.string,
};

TimeRangeAbsolute.defaultProps = {
  nameButtonSend: null,
  mode: null,
  language: null,
};
